/**
 * FairSiteHeader — RedisiginProductPage Header (1:719)
 * Desktop ≥1024 · Compact <1024 (Figma Size=Mibile: 1023 -)
 */

import type { FairIconName } from '@/ui/tokens/icons.data'
import { FAIR_RD_DESKTOP_MIN_WIDTH } from '@/ui/tokens/redesign.data'
import {
  PROFILE_MY_STYLE_ROUTE,
  PROFILE_NOTIFICATIONS_LIST_ROUTE,
  PROFILE_PEOPLE_ROUTE,
  PROFILE_SETTINGS_ROUTE,
  PROFILE_SIZES_ROUTE,
} from '@/router/routes'

export const FAIR_SITE_HEADER_SIZES = ['desktop', 'compact'] as const
export type FairSiteHeaderSize = (typeof FAIR_SITE_HEADER_SIZES)[number]

export interface FairSiteHeaderAudienceItem {
  label: string
  value: string
}

export interface FairSiteHeaderAction {
  id: string
  icon: FairIconName
  ariaLabel: string
}

export const FAIR_SITE_HEADER_DESKTOP_MIN_WIDTH = FAIR_RD_DESKTOP_MIN_WIDTH

export const FAIR_SITE_HEADER_DEFAULT_AUDIENCE: FairSiteHeaderAudienceItem[] = [
  { label: 'Women', value: 'women' },
  { label: 'Men', value: 'men' },
  { label: 'Kids', value: 'kids' },
]

export const FAIR_SITE_HEADER_DESKTOP_ACTIONS: FairSiteHeaderAction[] = [
  { id: 'style-space', icon: 'hanger', ariaLabel: 'Style space' },
  { id: 'search', icon: 'search', ariaLabel: 'Search' },
  { id: 'favorites', icon: 'favorite-outlined', ariaLabel: 'Favorites' },
  { id: 'account', icon: 'account', ariaLabel: 'Account' },
]

export const FAIR_SITE_HEADER_COMPACT_ACTIONS: FairSiteHeaderAction[] = [
  { id: 'search', icon: 'search', ariaLabel: 'Search' },
  { id: 'account', icon: 'account', ariaLabel: 'Account' },
]

/**
 * FE-026 — with the mobile bottom nav bar on screen (FE-024) the account entry
 * lives in the bar; the compact header keeps only search (Figma mobile
 * `Header` instances 11:36232 / 11:35637: menu · logo · search).
 */
export const FAIR_SITE_HEADER_COMPACT_BOTTOM_NAV_ACTIONS: FairSiteHeaderAction[] =
  FAIR_SITE_HEADER_COMPACT_ACTIONS.filter((action) => action.id !== 'account')

export function getFairSiteHeaderActions(
  size: FairSiteHeaderSize,
  hasBottomNav = false,
): FairSiteHeaderAction[] {
  if (size !== 'compact') return FAIR_SITE_HEADER_DESKTOP_ACTIONS
  return hasBottomNav
    ? FAIR_SITE_HEADER_COMPACT_BOTTOM_NAV_ACTIONS
    : FAIR_SITE_HEADER_COMPACT_ACTIONS
}

export function resolveFairSiteHeaderSize(
  width: number,
): FairSiteHeaderSize {
  return width >= FAIR_SITE_HEADER_DESKTOP_MIN_WIDTH ? 'desktop' : 'compact'
}

/**
 * T-145 — profile hover menu of the desktop header (Figma `3284:117068`).
 * Opens after the pointer rests on the profile icon for 150 ms, closes 300 ms after it
 * leaves both the icon and the menu (Figma «Behaviour»).
 */
export const FAIR_SITE_HEADER_ACCOUNT_MENU_OPEN_DELAY_MS = 150
export const FAIR_SITE_HEADER_ACCOUNT_MENU_CLOSE_DELAY_MS = 300

export type FairSiteHeaderAccountMenuItemId =
  | 'notifications'
  | 'style'
  | 'sizes'
  | 'people'
  | 'settings'
  | 'sign-out'
  | 'log-in'
  | 'sign-up'

export interface FairSiteHeaderAccountMenuItem {
  id: FairSiteHeaderAccountMenuItemId
  labelKey: string
  /** Where the row leads; sign-out and the guest rows act instead of navigating. */
  route?: string
}

/** Profile left navigation + Notifications; no Favourites / Lists (the heart opens them). */
export const FAIR_SITE_HEADER_ACCOUNT_MENU_MAIN: FairSiteHeaderAccountMenuItem[] = [
  { id: 'notifications', labelKey: 'fair_site_header.account_menu.notifications', route: PROFILE_NOTIFICATIONS_LIST_ROUTE },
  { id: 'style', labelKey: 'profile.nav.style', route: PROFILE_MY_STYLE_ROUTE },
  { id: 'sizes', labelKey: 'profile.nav.sizes', route: PROFILE_SIZES_ROUTE },
  { id: 'people', labelKey: 'profile.nav.people', route: PROFILE_PEOPLE_ROUTE },
  { id: 'settings', labelKey: 'profile.nav.settings', route: PROFILE_SETTINGS_ROUTE },
]

/** T-167 — Sign out alone under its own divider, so it is not hit by accident after Settings. */
export const FAIR_SITE_HEADER_ACCOUNT_MENU_SECONDARY: FairSiteHeaderAccountMenuItem[] = [
  { id: 'sign-out', labelKey: 'profile.nav.sign_out' },
]

/** P3 · guest — two rows, no identity block. */
export const FAIR_SITE_HEADER_GUEST_MENU: FairSiteHeaderAccountMenuItem[] = [
  { id: 'log-in', labelKey: 'fair_site_header.account_menu.log_in' },
  { id: 'sign-up', labelKey: 'fair_site_header.account_menu.sign_up' },
]

/** Badge text on the profile icon / Notifications row: nothing at zero, `9+` past nine. */
export function formatFairSiteHeaderUnread(count: number | null | undefined): string {
  const value = Math.max(0, Math.floor(Number(count) || 0))
  if (value === 0) return ''
  return value > 9 ? '9+' : String(value)
}
